<template>
    <div class="header-container">
        <div class="header-left">
            <div class="breadcrumb">
                <span class="welcome-text">欢迎回来</span>
                <icon-bytedance-color class="welcome-icon"/>
                <a-dropdown v-if="userInfo.nickname" position="br">
                    <div class="user-tag user-tag-clickable">
                        <icon-user class="user-icon" />
                        <span class="user-name">{{ userInfo.nickname }}</span>
                    </div>
                    <template #content>
                        <a-doption @click="handleLogout">
                            <template #icon><icon-export /></template>
                            <template #default>注销登录</template>
                        </a-doption>
                    </template>
                </a-dropdown>
            </div>
        </div>
        <div class="header-right">
            <span
                class="theme-toggle"
                :title="darkTheme ? '切换到浅色模式' : '切换到深色模式'"
                @click="toggleTheme(!darkTheme)"
            >
                <icon-sun v-if="darkTheme" />
                <icon-moon v-else />
            </span>
            <div class="time-display">
                <icon-clock-circle class="time-icon" />
                <span class="time-text">{{ formattedTime }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import {ref, computed} from 'vue';
import { IconBytedanceColor, IconExport, IconUser, IconClockCircle, IconSun, IconMoon } from '@arco-design/web-vue/es/icon';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { Message } from '@arco-design/web-vue';
import { logout } from '@/api';

export default {
    name: "HeaderView",
    components: {
        IconBytedanceColor,
        IconExport,
        IconUser,
        IconClockCircle,
        IconSun,
        IconMoon
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const nowTime = ref(new Date())

        setInterval(() => nowTime.value = new Date(), 1000);

        // 格式化时间
        const formattedTime = computed(() => {
            const date = nowTime.value;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
            const weekDay = weekDays[date.getDay()];
            return `${year}年${month}月${day}日 ${weekDay} ${hours}:${minutes}:${seconds}`;
        });

        // 获取当前用户信息
        const userInfo = computed(() => {
            return store.getters.userInfo || {};
        });
        
        const darkTheme = computed(() => store.getters.darkTheme);
        const toggleTheme = (v) => {
            store.commit('setDarkTheme', !!v);
        };

        const handleLogout = async () => {
            try {
                await logout();
            } catch (error) {
                console.error('后端注销失败:', error);
            } finally {
                store.dispatch('logout');
                Message.success('已注销登录');
                router.push('/login');
            }
        };

        return {
            nowTime,
            formattedTime,
            userInfo,
            darkTheme,
            toggleTheme,
            handleLogout
        }
    },
}
</script>

<style scoped>
.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  height: 100%;
}

/* 移动端：隐藏欢迎语、时间显示；用户放整行最右侧，主题切换在用户左边 */
@media (max-width: 768px) {
  .header-container {
    padding: 0;
    justify-content: flex-end;
    gap: 12px;
  }
  .welcome-text,
  .welcome-icon,
  .time-display {
    display: none !important;
  }
  .header-left {
    order: 2;
  }
  .header-right {
    order: 1;
  }
  .user-tag {
    margin-left: 0;
    padding: 5px 10px;
  }
}

.header-left {
  display: flex;
  align-items: center;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
}

.welcome-text {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}

.welcome-icon {
  font-size: 22px;
  color: #165dff;
}

.user-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-radius: 20px;
  margin-left: 12px;
}

.user-tag-clickable {
  cursor: pointer;
}

.user-tag-clickable:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
}

.user-icon {
  font-size: 14px;
  color: #667eea;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #667eea;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 18px;
  color: #4e5969;
  cursor: pointer;
  background: var(--color-fill-2);
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.theme-toggle:hover {
  background: rgba(102, 126, 234, 0.12);
  color: #667eea;
}

.time-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--color-fill-2);
  border-radius: 20px;
}

.time-icon {
  font-size: 16px;
  color: #86909c;
}

.time-text {
  font-size: 14px;
  font-weight: 500;
  color: #4e5969;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}
</style>

<style>
/* 暗色模式：头部文字与区块 */
body[arco-theme='dark'] .welcome-text {
  color: var(--color-text-1);
}
body[arco-theme='dark'] .user-tag {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
}
body[arco-theme='dark'] .user-tag-clickable:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%);
}
body[arco-theme='dark'] .user-name {
  color: var(--color-text-1);
}
body[arco-theme='dark'] .time-icon,
body[arco-theme='dark'] .time-text {
  color: var(--color-text-2);
}
body[arco-theme='dark'] .theme-toggle {
  color: var(--color-text-2);
}
body[arco-theme='dark'] .theme-toggle:hover {
  color: #ffd666;
}
</style>